import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { buildEmbeddedSignupCompleteUrl } from '../config/embeddedSignup';

type Status = 'processing' | 'success' | 'error';

/**
 * Rota pública /facebook/callback no host canônico (ex.: https://app.pooh.pro).
 *
 * Este é o `redirect_uri` configurado no Facebook Developers. A Meta volta
 * para cá com `?code=&state=` (formato OAuth tradicional). O state tem o
 * formato `slug:nonce` — extraímos o slug para descobrir qual API de tenant
 * deve receber o POST /complete (ex.: api.modelo.pooh.pro). O backend então
 * troca o code por access_token, lê WABA/phone_number_id e grava em
 * `whatsapp_cloud_api_configs`.
 */
const FacebookOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('processing');
  const [message, setMessage] = useState('Conectando sua conta WhatsApp Business...');
  const [errorCode, setErrorCode] = useState<string | undefined>();
  const [tenantHint, setTenantHint] = useState<string | undefined>();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    let cancelled = false;

    async function run(): Promise<void> {
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const errorReason = searchParams.get('error_reason');

      if (error) {
        if (!cancelled) {
          setStatus('error');
          setMessage(errorDescription || errorReason || 'Autorização cancelada ou negada pela Meta.');
          setErrorCode(error);
        }
        return;
      }

      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        if (!cancelled) {
          setStatus('error');
          setMessage(
            'A Meta voltou sem ?code= e ?state= na URL. Isso geralmente significa que o usuário cancelou o fluxo, ' +
              'ou que o redirect_uri configurado no Facebook Developers não corresponde exatamente a esta URL ' +
              `(${window.location.origin}/facebook/callback).`
          );
          setErrorCode('MISSING_PARAMS');
        }
        return;
      }

      const colon = state.indexOf(':');
      const tenantSlug = colon > 0 ? state.slice(0, colon) : '';
      if (!tenantSlug || !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(tenantSlug)) {
        if (!cancelled) {
          setStatus('error');
          setMessage('State OAuth inválido. Reinicie o fluxo pelo painel do cliente.');
          setErrorCode('INVALID_STATE');
        }
        return;
      }

      if (!cancelled) setTenantHint(tenantSlug);

      const completeUrl = buildEmbeddedSignupCompleteUrl(tenantSlug);
      if (!completeUrl) {
        if (!cancelled) {
          setStatus('error');
          setMessage(
            'No build do app canónico (ex.: app.pooh.pro) falta REACT_APP_EMBEDDED_SIGNUP_COMPLETE_URL_TEMPLATE. ' +
              'Exemplo: https://api.{tenant}.pooh.pro/api/v1/whatsapp-cloud-api/embedded-signup/complete — ' +
              'sem isto o navegador não sabe para qual api.{cliente}.pooh.pro enviar o code OAuth.'
          );
          setErrorCode('MISSING_COMPLETE_URL_TEMPLATE');
        }
        return;
      }

      try {
        const res = await fetch(completeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, state }),
        });

        const body = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(body.error || body.message || `Erro HTTP ${res.status}`);
        }

        const returnUrl = body.returnUrl as string | undefined;
        if (!cancelled && returnUrl) {
          setStatus('success');
          setMessage(`WhatsApp conectado! ${body.displayPhoneNumber ? `Número: ${body.displayPhoneNumber}` : ''}`);
          setTimeout(() => {
            window.location.href = returnUrl;
          }, 1200);
          return;
        }

        if (!cancelled) {
          setStatus('success');
          setMessage(
            (body.displayPhoneNumber ? `WhatsApp conectado — ${body.displayPhoneNumber}. ` : 'WhatsApp conectado com sucesso. ') +
              'Defina FRONTEND_URL no servidor da clínica para redirecionar automaticamente de volta ao painel.'
          );
          setTimeout(() => navigate('/login', { replace: true }), 3500);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setStatus('error');
          setMessage(`Falha ao finalizar com o servidor da clínica: ${msg}`);
          setErrorCode('COMPLETE_FAILED');
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        textAlign: 'center',
        bgcolor: 'background.default',
      }}
    >
      {status === 'processing' && (
        <>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Conectando...
          </Typography>
          <Typography color="text.secondary">{message}</Typography>
        </>
      )}
      {status === 'success' && (
        <>
          <Typography variant="h6" gutterBottom color="success.main">
            Sucesso
          </Typography>
          <Typography color="text.secondary">{message}</Typography>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Redirecionando de volta ao painel...
          </Typography>
        </>
      )}
      {status === 'error' && (
        <>
          <Typography variant="h6" gutterBottom color="error">
            Não foi possível conectar
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 480, mb: 2 }}>
            {message}
          </Typography>
          {errorCode && (
            <Typography variant="caption" color="text.disabled" sx={{ mb: 2, fontFamily: 'monospace' }}>
              {errorCode}
            </Typography>
          )}
          {tenantHint ? (
            <Button
              variant="contained"
              onClick={() => {
                const root = window.location.hostname.split('.').slice(-2).join('.');
                window.location.href = `https://${tenantHint}.${root || 'pooh.pro'}/whatsapp-cloud-api`;
              }}
            >
              Voltar ao painel
            </Button>
          ) : (
            <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
              Voltar
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default FacebookOAuthCallback;
